import { importFileToSheets, toCelldataSheet, isSheetS3StorageEnabled, uploadSheetVersion } from '../SheetEditor';
import { docxBlobToPlateValue } from '../PlateEditor/serializers/docxBlobToPlateValue';
import { txtBlobToPlateValue } from '../PlateEditor/serializers/txtBlobToPlateValue';
import { saveDocument } from '../../store/investor/investorApiHelper';
import { CUSTOM_DOC_TYPE } from '../../modules/Reports/constants';

const SHEET_MIMES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/csv',
];
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const TXT_MIME = 'text/plain';

type AddKind = 'sheet' | 'docx' | 'txt';

/**
 * Classify a file on a VALIDATED signal for Add-to-Reports: extension first,
 * else the passed mime (Ask AICA files can be extension-less — `fileType` is
 * the authoritative signal there). Anything outside {xlsx,xls,csv,docx,txt} —
 * pdf, legacy .doc, unknown — is NOT addable and must never reach mammoth or
 * save an empty CUSTOM doc (A-22).
 */
export function classifyAddable(fileName: string, fileType?: string): AddKind | null {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (['xlsx', 'xls', 'csv'].includes(ext)) return 'sheet';
    if (ext === 'docx') return 'docx';
    if (ext === 'txt') return 'txt';

    const mime = (fileType ?? '').split(';')[0].trim().toLowerCase();
    if (SHEET_MIMES.includes(mime)) return 'sheet';
    if (mime === DOCX_MIME) return 'docx';
    if (mime === TXT_MIME) return 'txt';
    return null;
}

const asFile = (file: File | Blob, fileName: string, fileType?: string): File =>
    file instanceof File ? file : new File([file], fileName, fileType ? { type: fileType } : undefined);

export interface AddFileToReportsArgs {
    file: File | Blob;
    fileName: string;
    fileType?: string;
    assesseeOrgId: string;
    investorOrgId?: string;
    createdBy?: string;
}

/**
 * Snapshot a file into the Reports module as a `documentType:CUSTOM` doc — the
 * shared entry point for Ask AICA / Data Room "Add to Reports" and the XLSX
 * drawer's primary button. Routes on `classifyAddable`; xlsx/xls/csv save the
 * exact shape `useSheetEditorDrawerLogic.handleSave` uses, docx/txt the exact
 * shape `ReportEditorRoute`'s persist uses (txt reuses the docx payload shape,
 * only the conversion step differs) — this is a direct snapshot (convert +
 * save), NOT `useReportsLogic.handleImportFile` (which only converts and opens
 * a drawer for the user to save later).
 */
export async function addFileToReports({
    file,
    fileName,
    fileType,
    assesseeOrgId,
    investorOrgId,
    createdBy,
}: AddFileToReportsArgs): Promise<any> {
    const kind = classifyAddable(fileName, fileType);
    if (!kind) {
        // Deterministic, handled rejection — never reaches mammoth, never saves
        // an empty doc (A-22). Caller toasts.
        throw new Error(`Add to Reports supports .docx and .xlsx/.xls/.csv only, not "${fileName}"`);
    }

    const name = fileName.replace(/\.(docx|xlsx?|csv|txt)$/i, '') || fileName;
    const base = {
        assesseeOrgId,
        ...(investorOrgId ? { investorOrgId } : {}),
        ...(createdBy ? { createdBy } : {}),
        documentType: CUSTOM_DOC_TYPE,
        name,
        description: '',
    };

    if (kind === 'sheet') {
        const sheets = (await importFileToSheets(asFile(file, fileName, fileType))).map(toCelldataSheet);
        // S3-backed snapshot when enabled — mirrors useSheetEditorDrawerLogic.handleSave:
        // upload the xlsx to S3 and persist a small storage ref, so a large workbook
        // added via "Add to Reports" (Data Room / Magic Upload / Ask AICA / preview
        // drawer) doesn't hit Mongo's 16 MB cap. No inline fallback on failure: a
        // workbook large enough to need S3 would fail the same way against Mongo's
        // cap, just with a more confusing error — the caller already toasts on throw.
        if (isSheetS3StorageEnabled()) {
            const ref = await uploadSheetVersion({ sheets, assesseeOrgId, investorOrgId });
            return saveDocument({ ...base, editor: 'sheet', formatType: 'XLSX', storage: ref });
        }
        return saveDocument({
            ...base,
            editor: 'sheet',
            formatType: 'XLSX',
            content: { version: 1, editor: 'sheet', value: sheets },
        });
    }

    const value = kind === 'txt' ? await txtBlobToPlateValue(file) : await docxBlobToPlateValue(file);
    return saveDocument({
        ...base,
        editor: 'plate',
        formatType: 'DOC',
        content: { version: 1, editor: 'plate', value },
    });
}

export interface AddFileUrlToReportsArgs extends Omit<AddFileToReportsArgs, 'file'> {
    fileUrl: string;
}

/**
 * Same as `addFileToReports`, but for callers that only hold a fetchable URL
 * (presigned S3 link or a local `blob:` URL) rather than an already-resolved
 * File/Blob — e.g. FileViewerDrawer's Add-to-Reports action on a preview
 * drawer, which never downloads the file up front.
 */
export async function addFileUrlToReports({
    fileUrl,
    fileName,
    fileType,
    assesseeOrgId,
    investorOrgId,
    createdBy,
}: AddFileUrlToReportsArgs): Promise<any> {
    if (!classifyAddable(fileName, fileType)) {
        throw new Error(`Add to Reports supports .docx and .xlsx/.xls/.csv only, not "${fileName}"`);
    }
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error('Failed to fetch file');
    const blob = await response.blob();
    return addFileToReports({ file: blob, fileName, fileType, assesseeOrgId, investorOrgId, createdBy });
}
